import { createFileRoute, Link } from '@tanstack/react-router';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { IncompleteConfig } from '@/features/admin/IncompleteConfig';
import { EmailRoutingSection } from '@/features/admin/emailRouting/EmailRoutingSection';
import { PlatformIdentityCard } from '@/features/admin/PlatformIdentityCard';
import { StatCard } from '@/shared/components/StatCard';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Divider, Group, SimpleGrid, Stack, Switch, Text, TextInput, rem,
} from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import {
  IconAlertTriangle, IconCircleCheck, IconCloudUpload, IconDeviceFloppy, IconFileText,
  IconInfoCircle, IconLayoutNavbar, IconLayoutSidebar, IconLayoutBottombar,
  IconRocket, IconShieldLock, IconTool, IconUserPlus, IconVersions,
} from '@tabler/icons-react';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import { useResourceList, useUpdateResource } from '@/shared/api/jsonapi/hooks';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileAdminSystem } from '@/features/admin/mobile/MobileAdminSystem';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/system')({ component: AdminSystemRoute });

/** Selettore: Sistema mobile (MaeDesign) vs pagina desktop. */
function AdminSystemRoute() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileAdminSystem /> : <AdminSystemPage />;
}

interface SetupRow extends Denormalized {
  type: 'setups';
  id: string;
  environment?: string;
  minAppVersion?: string;
  maintenance?: boolean;
  useRemoteFiles?: boolean;
  disableLog?: boolean;
  publicRegistration?: boolean;
  /** La testata resta ferma mentre il contenuto scorre. Vedi `features/admin/setupApp.ts`. */
  fixedHeader?: boolean;
  /** La colonna del menu resta ferma mentre il contenuto scorre. */
  fixedSidebar?: boolean;
  /**
   * Il piede della pagina resta fermo mentre il contenuto scorre.
   *
   * ⚠️ Oggi e' un `bool`, quindi l'interruttore qui sotto dice fermo/scorrevole e **non** sa dire
   * «non mostrarlo». Il terzo stato arriva quando il BE converte il campo in stringa: richiesta
   * aperta, concordata con Lares perche' il campo lo legge anche lui.
   */
  fixedFooter?: boolean;
}

type Patchable = Pick<SetupRow,
  'fixedHeader' | 'fixedSidebar' | 'fixedFooter'
  | 'minAppVersion' | 'maintenance' | 'useRemoteFiles' | 'disableLog' | 'publicRegistration'>;

type Tone = 'bordeaux' | 'azzurro' | 'green' | 'warning' | 'danger' | 'gray';

/** Coppia sfondo/foreground per l'icon-box, coerente con StatCard/MaeDesign. */
function accentBox(tone: Tone): { bg: string; fg: string } {
  switch (tone) {
    case 'bordeaux': return { bg: mw.brandTint, fg: mw.brand };
    case 'azzurro': return { bg: mw.accentTint, fg: mw.accentText };
    case 'green': return { bg: mw.successBg, fg: mw.success };
    case 'warning': return { bg: mw.warningBg, fg: mw.warning };
    case 'danger': return { bg: mw.dangerBg, fg: mw.danger };
    default: return { bg: mw.surface2, fg: mw.ink500 };
  }
}

/** Riga impostazione MaeDesign: icon-box tinto + titolo/descrizione + controllo a destra. */
function SettingRow({ icon, tone = 'azzurro', title, desc, control }: {
  icon: ReactNode;
  tone?: Tone;
  title: string;
  desc: string;
  control: ReactNode;
}) {
  const a = accentBox(tone);
  return (
    // `wrap` (non `nowrap`): su schermo stretto il controllo va a capo sotto la
    // descrizione invece di comprimere il testo in una colonna da due parole.
    <Group justify="space-between" wrap="wrap" align="flex-start" gap="md" py="md">
      <Group gap={12} wrap="nowrap" align="flex-start" style={{ flex: '1 1 240px', minWidth: 0 }}>
        <span style={{
          width: 38, height: rem(38), borderRadius: 11, background: a.bg, color: a.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {icon}
        </span>
        <div style={{ minWidth: 0 }}>
          <Text fw={600} style={{ color: mw.ink900, lineHeight: 1.25 }}>{title}</Text>
          <Text size="xs" c="dimmed" style={{ marginTop: 2 }}>{desc}</Text>
        </div>
      </Group>
      <div style={{ flexShrink: 0, paddingTop: 2 }}>{control}</div>
    </Group>
  );
}

function AdminSystemPage() {
  const { data, isLoading, isError } = useResourceList<SetupRow>('setups', { page: { size: 10 } });
  const update = useUpdateResource<SetupRow>('setups');

  // Prefer environment=web; fallback to first row.
  const setup = useMemo<SetupRow | undefined>(
    () => data?.data.find((s) => s.environment === 'web') ?? data?.data[0],
    [data],
  );

  const [draft, setDraft] = useState<Patchable>({});
  useEffect(() => {
    if (setup) {
      setDraft({
        minAppVersion: setup.minAppVersion ?? '',
        maintenance: !!setup.maintenance,
        useRemoteFiles: !!setup.useRemoteFiles,
        disableLog: !!setup.disableLog,
        publicRegistration: !!setup.publicRegistration,
        /* ⚠️ `!== false` e non `Boolean(...)`: il valore predefinito del BE è **acceso**, quindi
           una riga che non porta l'attributo va letta «fermo», non «scorre». Con `Boolean` un
           Setup mai configurato avrebbe mostrato gli interruttori spenti e il primo salvataggio
           avrebbe fatto scorrere testata e colonna senza che nessuno l'avesse chiesto. */
        fixedHeader: setup.fixedHeader !== false,
        fixedSidebar: setup.fixedSidebar !== false,
        fixedFooter: setup.fixedFooter !== false,
      });
    }
  }, [setup]);

  const dirty = useMemo(() => {
    if (!setup) return false;
    return (draft.minAppVersion ?? '') !== (setup.minAppVersion ?? '')
      || !!draft.maintenance !== !!setup.maintenance
      || !!draft.useRemoteFiles !== !!setup.useRemoteFiles
      || !!draft.disableLog !== !!setup.disableLog
      || !!draft.publicRegistration !== !!setup.publicRegistration
      || draft.fixedHeader !== (setup.fixedHeader !== false)
      || draft.fixedSidebar !== (setup.fixedSidebar !== false)
      || draft.fixedFooter !== (setup.fixedFooter !== false);
  }, [draft, setup]);

  const save = async () => {
    if (!setup) return;
    try {
      await update.mutateAsync({
        id: setup.id,
        attributes: {
          minAppVersion: (draft.minAppVersion ?? '').trim(),
          maintenance: !!draft.maintenance,
          useRemoteFiles: !!draft.useRemoteFiles,
          disableLog: !!draft.disableLog,
          publicRegistration: !!draft.publicRegistration,
          fixedHeader: draft.fixedHeader !== false,
          fixedSidebar: draft.fixedSidebar !== false,
          fixedFooter: draft.fixedFooter !== false,
        },
      });
      notifications.show({ color: 'green', message: 'Setup aggiornato' });
    } catch (e) {
      notifyError(e);
    }
  };

  return (
    <MaeShell>
      <MaePageHead
        title="Sistema"
        sub="Flag globali sul Setup. Le modifiche entrano in vigore immediatamente per tutte le aziende."
        icon={<IconTool size={23} />}
        actions={
          setup ? (
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={() => void save()}
              disabled={!dirty || update.isPending}
              loading={update.isPending}
            >
              Salva
            </Button>
          ) : undefined
        }
      />

      {/*
        La diagnostica di configurazione vive qui, in cima, ed è il primo blocco della pagina.

        Prima stava sulla **home**, per una ragione buona — «sepolta in una pagina di
        amministrazione non la guarda nessuno» — e per un effetto sbagliato: la home è la prima
        cosa che vede *chiunque*, e aprirla su un elenco di anomalie tecniche fa iniziare la
        giornata da un problema che riguarda una persona sola in tutta l'azienda.
        Qui invece è dove un superadmin arriva quando si chiede «cosa non è a posto», e in cima
        perché è la domanda che si fa prima di toccare i flag qui sotto.

        `IncompleteConfig` **sparisce da sé quando non c'è nulla**: su un'istanza sana questo
        blocco non occupa spazio.
      */}
      <IncompleteConfig />

      {/* Nessun `maw`: la pagina usa tutta la larghezza e le sezioni si distribuiscono su
          due colonne sopra ~1100px (vedi il wrapper flex più sotto). Con `maw={960}` su un
          monitor grande restava mezza pagina vuota. */}
      <Stack>
        {isLoading && (
          <>
            <InlineLoader label="Caricamento della configurazione di sistema…" minHeight={120} />
            <InlineLoader label="Caricamento della configurazione di sistema…" minHeight={160} />
          </>
        )}

        {isError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            Errore caricamento Setup.
          </Alert>
        )}

        {!isLoading && !isError && !setup && (
          <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
            Nessun record Setup trovato. Crealo dal database o tramite seed.
          </Alert>
        )}

        {setup && (
          <>
            {/* Stato live corrente (dai valori salvati, non dalla bozza). */}
            <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }}>
              <StatCard
                icon={setup.maintenance ? IconAlertTriangle : IconCircleCheck}
                color={setup.maintenance ? 'red' : 'green'}
                label="Stato sistema"
                value={setup.maintenance ? 'Manutenzione' : 'Operativo'}
              />
              <StatCard
                icon={IconUserPlus}
                color={setup.publicRegistration ? 'azzurro' : 'gray'}
                label="Registrazione"
                value={setup.publicRegistration ? 'Pubblica' : 'Riservata'}
              />
              <StatCard
                icon={IconFileText}
                color={setup.disableLog ? 'orange' : 'green'}
                label="Logging"
                value={setup.disableLog ? 'Disattivato' : 'Attivo'}
              />
              <StatCard
                icon={IconVersions}
                color="brand"
                label="Versione minima"
                value={setup.minAppVersion?.trim() || '—'}
              />
            </SimpleGrid>

            {/* Due colonne su schermo largo, una sotto ~1100px: `flex-wrap` con basi
                480/340 invece di un grid a colonne fisse (che andrebbe in overflow). */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: '3 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Zona critica: manutenzione. Card che si accende in rosso quando attiva. */}
            <div>
              <Text style={{ ...mwSectionLabel, marginBottom: 8 }}>Zona critica</Text>
              <Card
                withBorder
                padding="lg"
                radius="lg"
                style={draft.maintenance ? { borderColor: mw.danger, background: mw.dangerBg } : undefined}
              >
                <Stack gap="sm">
                  <SettingRow
                    icon={<IconShieldLock size={20} />}
                    tone="danger"
                    title="Modalità manutenzione"
                    desc="Blocca l'accesso all'applicazione a tutti gli utenti tranne i super-admin."
                    control={
                      <Switch
                        size="xl"
                        onLabel="ON"
                        offLabel="OFF"
                        color="red"
                        checked={!!draft.maintenance}
                        onChange={(e) => setDraft((p) => ({ ...p, maintenance: e.currentTarget.checked }))}
                        aria-label="Modalità manutenzione"
                      />
                    }
                  />
                  {draft.maintenance && (
                    <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                      Tutti gli utenti non super-admin verranno disconnessi al prossimo refresh.
                    </Alert>
                  )}
                </Stack>
              </Card>
            </div>

            {/* Flag operativi come lista di righe-impostazione con divisori. */}
            <div>
              <Text style={{ ...mwSectionLabel, marginBottom: 8 }}>Impostazioni operative</Text>
              <Card withBorder padding="lg" radius="lg">
                <Stack gap={0}>
                  <SettingRow
                    icon={<IconCloudUpload size={20} />}
                    tone="azzurro"
                    title="Usa file remoti"
                    desc="Serve gli asset statici da CDN remota invece che dal server locale."
                    control={
                      <Switch
                        checked={!!draft.useRemoteFiles}
                        onChange={(e) => setDraft((p) => ({ ...p, useRemoteFiles: e.currentTarget.checked }))}
                        aria-label="Usa file remoti"
                      />
                    }
                  />
                  <Divider color={mw.line} />
                  <SettingRow
                    icon={<IconFileText size={20} />}
                    tone="warning"
                    title="Disabilita log"
                    desc="Spegne l'invio dei log su MongoDB / log bus."
                    control={
                      <Switch
                        color="orange"
                        checked={!!draft.disableLog}
                        onChange={(e) => setDraft((p) => ({ ...p, disableLog: e.currentTarget.checked }))}
                        aria-label="Disabilita log"
                      />
                    }
                  />
                  <Divider color={mw.line} />
                  <SettingRow
                    icon={<IconUserPlus size={20} />}
                    tone="azzurro"
                    title="Registrazione pubblica"
                    desc="Consenti la self-registration di nuovi utenti."
                    control={
                      <Switch
                        checked={!!draft.publicRegistration}
                        onChange={(e) => setDraft((p) => ({ ...p, publicRegistration: e.currentTarget.checked }))}
                        aria-label="Registrazione pubblica"
                      />
                    }
                  />
                  <Divider color={mw.line} />
                  <SettingRow
                    icon={<IconVersions size={20} />}
                    tone="bordeaux"
                    title="Versione minima app"
                    desc="Le app con versione inferiore vengono forzate all'aggiornamento."
                    control={
                      <TextInput
                        placeholder="es. 1.5.0"
                        style={{ width: 160, maxWidth: '100%' }}
                        value={draft.minAppVersion ?? ''}
                        onChange={(e) => setDraft((p) => ({ ...p, minAppVersion: e.currentTarget.value }))}
                        styles={{ input: { fontVariantNumeric: 'tabular-nums' } }}
                        aria-label="Versione minima app"
                      />
                    }
                  />
                </Stack>
              </Card>
            </div>

            {/*
              ASPETTO DELLA SHELL — i tre interruttori che Lares ha in «Impostazioni → Aspetto».
              Fino al 09-09-2026 `fixedHeader` e `fixedSidebar` erano nel contratto e non li
              leggeva nessuno: comparivano solo in `api.gen.ts`. `fixedFooter` era il terzo, ed e'
              rimasto inutilizzato fino al 19-09-2026 perche' un piede non c'era proprio.

              ⚠️ `fixedFooter` diventera' una **stringa a tre valori** (fermo / scorrevole /
              nascosto): finche' e' un `bool`, «non mostrarlo» non si puo' chiedere da qui.

              ⚠️ Valgono per la riga `web`, che e' quella che questa pagina modifica, e
              descrivono la **shell desktop**. Sul telefono la barra e il cassetto sono un'altra
              cosa e non passano da qui.
            */}
            <div>
              <Text style={{ ...mwSectionLabel, marginBottom: 8 }}>Aspetto della shell</Text>
              <Card withBorder padding="lg" radius="lg">
                <Stack gap={0}>
                  <SettingRow
                    icon={<IconLayoutNavbar size={20} />}
                    tone="azzurro"
                    title="Testata ferma"
                    desc="Accesa, la barra in alto resta visibile mentre il contenuto scorre. Spenta, scorre via col contenuto e libera spazio in verticale."
                    control={
                      <Switch
                        checked={draft.fixedHeader !== false}
                        /* ⚠️ Il valore si prende **prima**: dentro l'aggiornatore, `currentTarget` puo' essere
                           gia' `null` — React lo azzera quando il gestore ritorna, e l'aggiornatore puo'
                           girare dopo. Vedi `tests/unit/eventoDentroAggiornatore.test.ts`; qui l'avevo
                           copiata sbagliata dagli interruttori accanto, che sono nel registro del debito. */
                        onChange={(e) => { const v = e.currentTarget.checked; setDraft((p) => ({ ...p, fixedHeader: v })); }}
                        aria-label="Testata ferma"
                      />
                    }
                  />
                  <Divider color={mw.line} />
                  <SettingRow
                    icon={<IconLayoutSidebar size={20} />}
                    tone="azzurro"
                    title="Colonna del menu ferma"
                    desc="Accesa, il menu resta al suo posto e scorre per conto suo quando le voci non stanno. Spenta, scorre via col contenuto."
                    control={
                      <Switch
                        checked={draft.fixedSidebar !== false}
                        /* ⚠️ Il valore si prende **prima**: dentro l'aggiornatore, `currentTarget` puo' essere
                           gia' `null` — React lo azzera quando il gestore ritorna, e l'aggiornatore puo'
                           girare dopo. Vedi `tests/unit/eventoDentroAggiornatore.test.ts`; qui l'avevo
                           copiata sbagliata dagli interruttori accanto, che sono nel registro del debito. */
                        onChange={(e) => { const v = e.currentTarget.checked; setDraft((p) => ({ ...p, fixedSidebar: v })); }}
                        aria-label="Colonna del menu ferma"
                      />
                    }
                  />
                  <Divider color={mw.line} />
                  <SettingRow
                    icon={<IconLayoutBottombar size={20} />}
                    tone="azzurro"
                    title="Piede della pagina fermo"
                    desc="Acceso, la striscia in fondo resta visibile. Spento, sta in coda al contenuto e se ne va scorrendo. Sul telefono il piede non c'e' comunque: al suo posto c'e' la barra di navigazione."
                    control={
                      <Switch
                        checked={draft.fixedFooter !== false}
                        /* ⚠️ Il valore si prende **prima**: vedi l'interruttore qui sopra. */
                        onChange={(e) => { const v = e.currentTarget.checked; setDraft((p) => ({ ...p, fixedFooter: v })); }}
                        aria-label="Piede della pagina fermo"
                      />
                    }
                  />
                  {(draft.fixedHeader === false || draft.fixedSidebar === false) && (
                    <>
                      <Divider color={mw.line} />
                      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />} mt="sm">
                        Con testata o menu scorrevoli, <strong>l'intestazione della pagina</strong> —
                        titolo, contatori, filtri — scorre via insieme a loro. Su un elenco lungo si
                        perde di vista su cosa si sta filtrando: e' il motivo per cui il valore
                        predefinito e' «ferma».
                      </Alert>
                    </>
                  )}
                </Stack>
              </Card>
            </div>
            </div>

            <div style={{ flex: '2 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Le notifiche del vecchio changelog: dal 26-10-2026 le note di rilascio sono circolari
                (task 2055), e canali e revisione si decidono per azienda in «Regole di lavoro». Le
                colonne `changelogDefault*` di questa riga il sistema le toglie col secondo rilascio. */}
            <div>
              <Text style={{ ...mwSectionLabel, marginBottom: 8 }}>Note di rilascio</Text>
              <Card withBorder padding="lg" radius="lg">
                <Group gap={12} wrap="nowrap" align="flex-start">
                  <span style={{
                    width: 38, height: 38, borderRadius: 11, background: mw.brandTint, color: mw.brand,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IconRocket size={20} />
                  </span>
                  <Stack gap={6}>
                    <Text fw={600} style={{ color: mw.ink900, lineHeight: 1.25 }}>Sono circolari</Text>
                    <Text size="xs" c="dimmed">
                      Le note di rilascio si scrivono e si pubblicano fra le circolari, con l’etichetta «Rilascio». Notifiche,
                      e-mail e revisione si decidono in «Regole di lavoro» › «Circolari».
                    </Text>
                    <Group gap={8}>
                      <Button component={Link} to="/circulars/register" size="xs" variant="light">Apri le circolari</Button>
                      <Button renderRoot={(props) => <Link to="/organization/work-rules" search={{ section: 'circulars' }} {...props} />} size="xs" variant="default">Regole delle circolari</Button>
                    </Group>
                  </Stack>
                </Group>
              </Card>
            </div>
            </div>
            </div>

            {/* Footer: metadati setup + stato modifiche. */}
            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Group gap="xs" wrap="wrap">
                <Badge variant="light" color="gray" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  Setup {setup.id}
                </Badge>
                {setup.environment && (
                  <Badge variant="light" color="azzurro">env · {setup.environment}</Badge>
                )}
              </Group>
              <Group gap="sm">
                {dirty && (
                  <Text size="sm" style={{ color: mw.warning, fontWeight: 600 }}>
                    Modifiche non salvate
                  </Text>
                )}
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={() => void save()}
                  disabled={!dirty || update.isPending}
                  loading={update.isPending}
                >
                  Salva
                </Button>
              </Group>
            </Group>
          </>
        )}

        {/* Posta in uscita: endpoint e «Salva» suoi (vale per tutto l'ambiente, non per un canale di
            `setups`), quindi resta fuori dal blocco del Setup e c'è anche se quello non si legge. */}
        {/* Task 2061: c'è solo se il sistema conosce i campi nuovi della riga Setup. */}
        {setup && 'supportEmail' in setup && <PlatformIdentityCard setup={setup} />}
        <EmailRoutingSection />
      </Stack>
    </MaeShell>
  );
}
